import React, { useEffect, useState } from 'react'
import { ChevronDown, type LucideIcon } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Tooltip, useIsTruncated } from '@/components/ui/tooltip'

export type SectionMeta = {
  id: string
  label: string
  icon: LucideIcon
  desc: string
}

export type NavNode =
  | { type: 'leaf'; id: string }
  | { type: 'group'; id: string; label: string; icon: LucideIcon; children: string[] }

type Props = {
  nodes: NavNode[]
  sectionsById: Record<string, SectionMeta>
  active: string
  onSelect: (id: string) => void
  // Optional count badge per leaf section id (e.g. pending approvals) — most
  // tabs have none, so this is a sparse map rather than a field on every node.
  badges?: Record<string, number>
}

const rowClass = (indented: boolean) =>
  `flex items-center gap-2 rounded-xl text-sm font-medium transition w-full text-left ${indented ? 'py-2 pl-9 pr-3' : 'py-2.5 px-3'}`

type NavLeafProps = {
  section: SectionMeta
  indented: boolean
  isActive: boolean
  onSelect: (id: string) => void
  badgeCount?: number | undefined
}

const NavLeaf: React.FC<NavLeafProps> = ({ section, indented, isActive, onSelect, badgeCount }) => {
  const { currentTheme: t } = useTheme()
  const [labelRef, isTruncated] = useIsTruncated<HTMLSpanElement>()
  const Icon = section.icon

  return (
    <Tooltip content={section.label} disabled={!isTruncated}>
      <button
        type="button"
        onClick={() => onSelect(section.id)}
        className={rowClass(indented)}
        style={{ backgroundColor: isActive ? `${t.accent}18` : 'transparent', color: isActive ? t.accent : t.text }}
      >
        <Icon size={14} className="shrink-0" />
        <span ref={labelRef} className="flex-1 min-w-0 truncate">{section.label}</span>
        {!!badgeCount && (
          <span
            className="shrink-0 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] font-semibold"
            style={{ backgroundColor: isActive ? t.accent : t.hoverBackground, color: isActive ? t.buttonText : t.mutedText }}
          >
            {badgeCount}
          </span>
        )}
      </button>
    </Tooltip>
  )
}

type NavGroupHeaderProps = {
  label: string
  icon: LucideIcon
  isOpen: boolean
  onToggle: () => void
}

const NavGroupHeader: React.FC<NavGroupHeaderProps> = ({ label, icon: Icon, isOpen, onToggle }) => {
  const { currentTheme: t } = useTheme()
  const [labelRef, isTruncated] = useIsTruncated<HTMLSpanElement>()

  return (
    <Tooltip content={label} disabled={!isTruncated}>
      <button
        type="button"
        onClick={onToggle}
        className={rowClass(false)}
        style={{ backgroundColor: 'transparent', color: t.text }}
      >
        <Icon size={14} className="shrink-0" />
        <span ref={labelRef} className="flex-1 min-w-0 truncate">{label}</span>
        <ChevronDown
          size={14}
          className="shrink-0 transition-transform duration-200"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>
    </Tooltip>
  )
}

export const SettingsNav: React.FC<Props> = ({ nodes, sectionsById, active, onSelect, badges }) => {
  const [openGroups, setOpenGroups] = useState<string[]>(() =>
    nodes.filter((n): n is Extract<NavNode, { type: 'group' }> => n.type === 'group' && n.children.includes(active)).map((n) => n.id),
  )

  // Keeps whichever group owns the current tab expanded — covers landing via
  // ?tab=<id> and any future in-page navigation, without collapsing groups
  // the user has already opened themselves.
  useEffect(() => {
    const owningGroup = nodes.find(
      (n): n is Extract<NavNode, { type: 'group' }> => n.type === 'group' && n.children.includes(active),
    )
    if (owningGroup) {
      setOpenGroups((prev) => (prev.includes(owningGroup.id) ? prev : [...prev, owningGroup.id]))
    }
  }, [active, nodes])

  const renderLeaf = (id: string, indented: boolean) => {
    const section = sectionsById[id]
    if (!section) return null
    return (
      <NavLeaf
        key={id}
        section={section}
        indented={indented}
        isActive={active === id}
        onSelect={onSelect}
        badgeCount={badges?.[id]}
      />
    )
  }

  return (
    <nav className="flex flex-col gap-1.5">
      {nodes.map((node) => {
        if (node.type === 'leaf') return renderLeaf(node.id, false)

        const isOpen = openGroups.includes(node.id)
        return (
          <div key={node.id}>
            <NavGroupHeader
              label={node.label}
              icon={node.icon}
              isOpen={isOpen}
              onToggle={() => setOpenGroups((prev) => (isOpen ? prev.filter((g) => g !== node.id) : [...prev, node.id]))}
            />
            {isOpen && <div className="mt-1 flex flex-col gap-1.5">{node.children.map((childId) => renderLeaf(childId, true))}</div>}
          </div>
        )
      })}
    </nav>
  )
}

export default React.memo(SettingsNav)
